import React from "react";
import type { BillingData } from "../../../hooks/useMyAccountData";

interface PaymentMethodModalProps {
  billingData: BillingData;
  onClose: () => void;
  handleUpdatePayment: () => void;
}

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  billingData,
  onClose,
  handleUpdatePayment,
}) => {
  const methods = [
    {
      value: "card",
      icon: "💳",
      label: "Credit / Debit Card",
      desc: "Visa, Mastercard, Amex",
      testId: "payment-option-card",
    },
    {
      value: "bank",
      icon: "🏦",
      label: "Bank Transfer",
      desc: "BECS Direct Debit (AU bank accounts)",
      testId: "payment-option-bank",
    },
    {
      value: "applepay",
      icon: "",
      label: "Apple Pay",
      desc: "Pay with Apple Pay on supported devices",
      testId: "payment-option-applepay",
    },
    {
      value: "googlepay",
      icon: "G",
      label: "Google Pay",
      desc: "Pay with Google Pay on supported devices",
      testId: "payment-option-googlepay",
    },
  ] as const;

  return (
    <div
      className="modal-overlay"
      data-testid="payment-modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-content"
        data-testid="payment-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" data-testid="payment-modal-header">
          <h3>Update Payment Method</h3>
          <button
            className="close-button"
            data-testid="payment-modal-close-button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        <div className="modal-body" data-testid="payment-modal-body">
          <p
            style={{
              color: "#6b7280",
              fontSize: "14px",
              marginBottom: "20px",
            }}
          >
            Select your preferred payment method. All methods are processed
            securely via Stripe.
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {methods.map((m) => (
              <label
                key={m.value}
                data-testid={m.testId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "16px",
                  border:
                    billingData.paymentMethod === m.value
                      ? "2px solid #3b82f6"
                      : "1px solid #e5e7eb",
                  borderRadius: "10px",
                  cursor: "pointer",
                  background:
                    billingData.paymentMethod === m.value ? "#eff6ff" : "#fff",
                  transition: "all 0.15s",
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value={m.value}
                  checked={billingData.paymentMethod === m.value}
                  readOnly
                  style={{ accentColor: "#3b82f6" }}
                />
                <span style={{ fontSize: "22px" }}>{m.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "15px" }}>
                    {m.label}
                  </div>
                  <div style={{ color: "#6b7280", fontSize: "13px" }}>
                    {m.desc}
                  </div>
                </div>
              </label>
            ))}
          </div>

          <p
            style={{
              color: "#9ca3af",
              fontSize: "12px",
              marginTop: "16px",
              textAlign: "center",
            }}
          >
            🔒 All payments are securely processed by Stripe. ARCaml never
            stores your payment details.
          </p>
        </div>
        <div className="modal-footer" data-testid="payment-modal-footer">
          <button
            className="action-button secondary"
            data-testid="payment-modal-cancel-button"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="action-button primary"
            data-testid="payment-modal-save-button"
            onClick={handleUpdatePayment}
          >
            Save Payment Method
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
